import { useEffect, useRef, useState } from 'react';
import { Button } from './Button';

/**
 * A remove button that asks first.
 *
 * Every delete in the app used to act on the first click, from a red
 * button sitting beside Cancel and Save. Between 16 and 17 September every
 * machine in the register was removed that way — thirty-five diaries were
 * left pointing at machines that no longer existed, and the dashboard
 * reported a fleet of nought.
 *
 * The first click turns the button into a question; the second answers
 * it. Left alone, the question withdraws itself after a few seconds, so a
 * stray tap never leaves a loaded "Yes" waiting under the next one.
 *
 * `onConfirm` returns whether the removal went through. The caller closes
 * its drawer only on success — a refusal (say, a machine that has history)
 * leaves the record open, with the reason on screen, instead of closing
 * over it as if it had worked.
 */

interface Props {
  onConfirm: () => Promise<boolean>;
  /** What the button says before anyone has clicked. */
  label?: string;
  /** The question, e.g. "Remove EX-01?" */
  question?: string;
  /** Row-level buttons are smaller and quieter than a drawer footer's. */
  compact?: boolean;
  disabled?: boolean;
}

const WITHDRAW_AFTER_MS = 6000;

export function ConfirmRemove({
  onConfirm, label = 'Remove', question, compact, disabled,
}: Props) {
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  function ask() {
    setAsking(true);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setAsking(false), WITHDRAW_AFTER_MS);
  }

  async function confirm() {
    window.clearTimeout(timer.current);
    setBusy(true);
    try {
      await onConfirm();
    } finally {
      setBusy(false);
      setAsking(false);
    }
  }

  const size = compact ? { minHeight: 28 } : undefined;

  if (!asking) {
    return compact ? (
      <button className="btn btn--ghost" style={size} disabled={disabled} onClick={ask}>
        {label}
      </button>
    ) : (
      <Button variant="danger" disabled={disabled} onClick={ask}>{label}</Button>
    );
  }

  return (
    <span className="row" style={{ gap: 'var(--sp-2)', alignItems: 'center' }}>
      {question && !compact && <span className="small">{question}</span>}
      <Button variant="danger" style={size} loading={busy} onClick={() => void confirm()}>
        Yes, {label.toLowerCase()}
      </Button>
      <Button variant="secondary" style={size} disabled={busy} onClick={() => setAsking(false)}>
        Keep
      </Button>
    </span>
  );
}
